import {
  type MultiCurveCandidate,
  useDemandData,
} from "@inrange/building-manager-api-client";
import { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import { HhDataTempFile } from "./DemandFileSummaryRow";

interface Props {
  file: HhDataTempFile;
  candidates: MultiCurveCandidate[];
  reason: string;
  onClose: () => void;
  // Called after the split kicks off with the new child guids (+ their operationKeys)
  // so the parent can register them into its Auto-process polling / AI-coach loop.
  // Fired whenever at least one curve started, even if some failed; the modal stays
  // open to show any failures and closes via onClose.
  onSplitComplete: (
    newGuids: { label: string; fileGuid: string; operationKey: string }[]
  ) => void;
}

/**
 * Multi-curve split modal: shown when /detect_multi_curve flags the input file
 * as containing data for multiple sites. Operator picks which curves to
 * materialise; we call /split_into_curves which produces N new guids each with
 * its own copy of the input file + per-curve config + Auto-mode kick-off.
 *
 * Backs design §4.7.
 */
const MultiCurveSplitModal = ({
  file,
  candidates,
  reason,
  onClose,
  onSplitComplete,
}: Props) => {
  const { splitIntoCurves } = useDemandData();

  // Default-select all candidates
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(candidates.map((c) => c.label))
  );
  const [deleteSource, setDeleteSource] = useState<boolean>(true);
  const [splitResult, setSplitResult] = useState<{
    newGuids: { label: string; fileGuid: string; operationKey: string }[];
    failed: { label: string; error: string }[];
  } | null>(null);

  const sourceTag = useMemo(
    () => candidates[0]?.source ?? "sheet",
    [candidates]
  );

  const toggleAll = (checked: boolean) => {
    setSelected(
      checked ? new Set(candidates.map((c) => c.label)) : new Set()
    );
  };

  const toggleOne = (label: string, checked: boolean) => {
    const next = new Set(selected);
    if (checked) next.add(label);
    else next.delete(label);
    setSelected(next);
  };

  const handleSplit = async () => {
    if (selected.size === 0) {
      alert("Select at least one curve to process.");
      return;
    }
    const curves = candidates
      .filter((c) => selected.has(c.label))
      .map((c) => ({ label: c.label, config: c.config }));
    try {
      const result = await splitIntoCurves.mutateAsync({
        fileGuid: file.fileGuid,
        request: { curves, deleteSource },
      });
      setSplitResult(result);
      // Register the successfully-started children with the parent so they enter the
      // Auto-process polling + AI-coach loop. Done regardless of partial failures.
      if (result.newGuids.length > 0) {
        onSplitComplete(result.newGuids);
      }
    } catch (e: any) {
      alert(`Split failed: ${e?.response?.data?.error || e}`);
    }
  };

  const confidenceBadge = (c: MultiCurveCandidate) => {
    const bg =
      c.confidence === "high"
        ? "success"
        : c.confidence === "medium"
          ? "warning"
          : "secondary";
    return <Badge bg={bg}>{c.confidence}</Badge>;
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          Multi-curve file detected
          <Badge bg="info" className="ms-2">
            {sourceTag === "sheet"
              ? "sheet-per-site"
              : sourceTag === "row_meter"
                ? "row-stacked meters"
                : sourceTag === "measurement_point"
                  ? "meter-point columns"
                  : "column-per-site"}
          </Badge>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!splitResult && (
          <>
            <p className="mb-2">
              <strong>{file.fileName}</strong> appears to contain data for
              multiple sites: {reason}.
            </p>
            <p className="text-muted small">
              Producing N output curves from one file. Each curve gets its own
              processed-data guid with a copy of the input file. Once split,
              each curve runs through Auto processing independently.
            </p>

            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                <strong>{candidates.length} curves detected.</strong>{" "}
                <span className="text-muted">{selected.size} selected.</span>
              </div>
              <div>
                <Button
                  size="sm"
                  variant="link"
                  onClick={() => toggleAll(true)}
                >
                  Select all
                </Button>
                <Button
                  size="sm"
                  variant="link"
                  onClick={() => toggleAll(false)}
                >
                  Select none
                </Button>
              </div>
            </div>

            <Table size="sm" responsive bordered>
              <thead>
                <tr>
                  <th style={{ width: 40 }}></th>
                  <th>Label</th>
                  <th style={{ width: 100 }}>Source</th>
                  <th style={{ width: 100 }}>Confidence</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => (
                  <tr key={c.label}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selected.has(c.label)}
                        onChange={(e) => toggleOne(c.label, e.target.checked)}
                      />
                    </td>
                    <td>
                      <code>{c.label}</code>
                      {c.reasons.length > 0 && (
                        <div className="text-muted small">{c.reasons[0]}</div>
                      )}
                    </td>
                    <td>{c.source}</td>
                    <td>{confidenceBadge(c)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <Form.Check
              type="checkbox"
              id="delete-source"
              className="mt-3"
              checked={deleteSource}
              onChange={(e) => setDeleteSource(e.target.checked)}
              label={
                <span>
                  Delete the source file after split is successful{" "}
                  <span className="text-muted small">
                    (recommended - the individual curves replace the original)
                  </span>
                </span>
              }
            />
          </>
        )}

        {splitResult && (
          <>
            <Alert
              variant={
                splitResult.failed.length === 0 ? "success" : "warning"
              }
            >
              Split into <strong>{splitResult.newGuids.length}</strong> curves;
              each one is now Auto-processing.
              {splitResult.failed.length > 0 && (
                <>
                  {" "}
                  <strong>{splitResult.failed.length}</strong> failed to start.
                </>
              )}
            </Alert>
            {splitResult.newGuids.length > 0 && (
              <Table size="sm">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>New file guid</th>
                  </tr>
                </thead>
                <tbody>
                  {splitResult.newGuids.map((g) => (
                    <tr key={g.fileGuid}>
                      <td>
                        <code>{g.label}</code>
                      </td>
                      <td>
                        <code style={{ fontSize: "0.8em" }}>{g.fileGuid}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {splitResult.failed.length > 0 && (
              <Table size="sm" className="mt-3">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {splitResult.failed.map((f, i) => (
                    <tr key={i}>
                      <td>
                        <code>{f.label}</code>
                      </td>
                      <td className="text-danger">{f.error}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!splitResult && (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel - process as single curve
            </Button>
            <Button
              variant="success"
              onClick={handleSplit}
              disabled={splitIntoCurves.isLoading || selected.size === 0}
            >
              {splitIntoCurves.isLoading ? (
                <>
                  <Spinner size="sm" animation="border" /> Splitting...
                </>
              ) : (
                `Split into ${selected.size} curve(s)`
              )}
            </Button>
          </>
        )}
        {splitResult && (
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default MultiCurveSplitModal;
